import { Injectable, inject } from '@angular/core';

import { I18NextPipe } from 'angular-i18next';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, of } from 'rxjs';

import { Currency, CurrencyConfig } from '@hdms/shared/common-types';
import { CurrencyService, LogoutDetailsResponse, OauthService, UserContextService } from '@hdms/shared/data-access';
import { MetaMenuType, MetaNavItem, MetaNavItemsMap } from '@hdms/shared/page-frame/types';
import { IconsName } from '@hdms/shared/ui-core';
import { CountryLanguage, EnvironmentService, PageConfigService, jsonEqual, pickSubset } from '@hdms/shared/util-core';

import { COMPRESSED_META_ITEMS, FULL_META_ITEMS } from '../config/header-config.const';

interface HeaderCurrencyLink extends Currency {
  name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class HeaderConfigService {
  readonly isCompressed$ = inject(PageConfigService).isCompressedPageLayout$;

  private readonly envService = inject(EnvironmentService);
  private readonly currencyService = inject(CurrencyService);
  private readonly oauthService = inject(OauthService);
  private readonly i18next = inject(I18NextPipe);
  private readonly userContext = inject(UserContextService);

  private readonly languageSubject = new BehaviorSubject(this.envService.language);

  get loginUrl$(): Observable<string> {
    return this.oauthService.getLoginUrl$();
  }

  get logoutDetails$(): Observable<LogoutDetailsResponse> {
    return this.oauthService.getLogoutDetails$();
  }

  get languages(): CountryLanguage[] {
    return this.envService.availableLanguages;
  }

  get activeLanguage(): CountryLanguage {
    return this.languages.find(({ language }) => language === this.languageSubject.value);
  }

  get hasManyLanguages(): boolean {
    return this.languages.length > 1;
  }

  get hasManyCurrencies(): boolean {
    return this.envService.currencies.length > 1;
  }

  get currencyConfig$(): Observable<CurrencyConfig> {
    return this.currencyService.getConfig$();
  }

  get metaNavConfig$(): Observable<[MetaNavItemsMap, MetaNavItem[]]> {
    return combineLatest([this.getMetaLink$(), this.isCompressed$]).pipe(
      distinctUntilChanged(jsonEqual),
      map(([linksMap, isCompressed]) => {
        const metaItems = this.getMetaItemsBasedOnUserRole();
        const itemsMap = pickSubset(linksMap, metaItems);
        return [
          itemsMap,
          (isCompressed ? COMPRESSED_META_ITEMS : metaItems).map(type => itemsMap[type]).filter(Boolean)
        ];
      })
    );
  }

  switchLanguage(lang: string): void {
    this.languageSubject.next(lang);
  }

  switchCurrency(code: string): void {
    this.currencyService.switchCurrency(code);
  }

  public getManageMyToolsLink(): MetaNavItem {
    return {
      title: this.i18next.transform('header.meta.manageMyTools'),
      icon: IconsName.SPANNER_ANGLED,
      menuType: MetaMenuType.MANAGE_MY_TOOLS,
      trackingName: 'ManageMyTools'
    };
  }

  private getMetaItemsBasedOnUserRole(): MetaMenuType[] {
    let items = [...FULL_META_ITEMS];

    if (this.userContext.isEVoucherBackofficeUser) {
      // remove cart link for eVoucher Backoffice users
      items = items.filter(item => item !== MetaMenuType.CART);
    }

    return items;
  }

  private getMetaLink$(): Observable<MetaNavItemsMap> {
    return combineLatest([of(this.getMetaLinks()), this.currencyConfig$, this.languageSubject]).pipe(
      map(([itemsMap, { activeCurrency }]) => ({
        ...itemsMap,
        ...this.getLanguageLink(),
        ...this.getCurrencyLink(activeCurrency)
      }))
    );
  }

  private getMetaLinks(): MetaNavItemsMap {
    return {
      [MetaMenuType.CONTACT]: {
        title: this.i18next.transform('header.meta.contact'),
        icon: IconsName.CONTACT,
        menuType: MetaMenuType.CONTACT,
        trackingName: 'Contact'
      },
      [MetaMenuType.CART]: {
        title: this.i18next.transform('header.meta.cart'),
        menuType: MetaMenuType.CART,
        trackingName: 'Cart'
      }
    };
  }

  private getLanguageLink(): MetaNavItemsMap {
    return this.hasManyLanguages
      ? {
          [MetaMenuType.LANG]: {
            title: this.i18next.transform('header.meta.language', { code: this.languageSubject.value }),
            titleShort: this.activeLanguage.language,
            headerLabel: this.i18next.transform('header.meta.languageHeaderLabel'),
            menuType: MetaMenuType.LANG,
            icon: IconsName.WORLD,
            trackingName: 'Language'
          }
        }
      : {};
  }

  private getCurrencyLink(activeCurrency: HeaderCurrencyLink): MetaNavItemsMap {
    // needed to match existing translation keys
    if (activeCurrency) {
      activeCurrency.name = activeCurrency.code;
    }

    return this.hasManyCurrencies
      ? {
          [MetaMenuType.CURRENCY]: {
            title: this.i18next.transform(
              `header.meta.${activeCurrency.rate ? 'currency' : 'currencyCodeOnly'}`,
              activeCurrency
            ),
            titleShort: activeCurrency.rate
              ? this.i18next.transform('header.meta.currencyShort', activeCurrency)
              : activeCurrency.code,
            headerLabel: this.i18next.transform('header.meta.currencyHeaderLabel'),
            menuType: MetaMenuType.CURRENCY,
            icon: IconsName.CURRENCY,
            trackingName: 'Currency'
          }
        }
      : {};
  }
}
